import * as React from 'react';
import { Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import GenericSelect from './GenericSelect';
import { PartnerSourceData } from '../../types';
import ViewInDocumentButton from '../ViewInDocumentButton/ViewInDocumentButton';
import { DocumentHelper } from '../../../custom_shared/misc';
import './PartnerSelect.less';

type Props = Omit<React.ComponentProps<typeof GenericSelect>, 'options' | 'extra'> & { plainLabels?: boolean };

const PartnerSelect: React.FC<Props> = ({ plainLabels, ...props }) => {
    const { sourceData } = props.input.sourceProvider;
    const options = React.useMemo(
        () =>
            sourceData.map((data: PartnerSourceData) => {
                const location = [data.partnerState?.trim(), data.partnerCountry?.trim()].filter(Boolean).join(', ');

                return {
                    value: data.value,
                    title: data.text,
                    label:
                        !plainLabels && data.partnerName ? (
                            <span>
                                {data.partnerName}
                                {location && <Typography.Text type="secondary">{` · ${location}`}</Typography.Text>}
                            </span>
                        ) : (
                            data.text
                        )
                };
            }),
        [sourceData, plainLabels]
    );

    const extractedName = props.input.meta?.extractedPartnerName;
    const showEvidence = !props.input.hasValue && typeof extractedName === 'string' && extractedName.trim().length > 0;
    const extra = showEvidence ? (
        <Typography.Text type="secondary" className="partner-extraction-evidence">
            <span className="partner-extraction-prefix">Found in document:</span>{' '}
            <span className="partner-extraction-name" title={extractedName}>
                {DocumentHelper.hasValidBlockType(props.input) ? (
                    <ViewInDocumentButton input={props.input}>{extractedName}</ViewInDocumentButton>
                ) : (
                    extractedName
                )}
            </span>{' '}
            <Tooltip
                trigger={['hover', 'focus']}
                title="Partner found in the document, but no exact SICS partner was identified. Select the correct partner manually."
            >
                <InfoCircleOutlined
                    className="partner-extraction-info"
                    tabIndex={0}
                    aria-label="Unresolved partner information"
                />
            </Tooltip>
        </Typography.Text>
    ) : undefined;

    return <GenericSelect {...props} options={options} extra={extra} />;
};

export default observer(PartnerSelect);
